import {Tr} from "@/i18n/i18n";
import Scene = Phaser.Scene;
import {Keys} from "@/constants/key";
import TextUtils from "@/utils/text-utils";
import {UpdateInfo} from "@/store/artifact/types";
import ArtifactUtils from "@/utils/artifact-utils";
import TimeUtils from "@/utils/time-utils";
import PricePanel from "@/scenes.ui/price-panel";
import Container = Phaser.GameObjects.Container;
import {ArtifactBasePanel} from "@/scenes.ui/artifact-base-panel";
import {SquareButton} from "@/scenes.ui/square-button";
import {Fonts} from "@/constants/font";
import PlayerStore from "@/store/player";
import SpriteUtils from "@/utils/sprite-utils";
import CU from "@/utils/c-u";
import {BottomForcesPanel} from "@/scenes.ui/bottom-forces-panel";
import OnceCaller from "@/utils/once-caller";

export class UpdateArtifactPanel extends ArtifactBasePanel {

    constructor(scene: Scene, canvas: HTMLCanvasElement, ui: UpdateInfo,
                takeOffCallback: () => unknown, improveCallback: () => unknown, cantUpdateCallback: (alertText: string) => unknown,
                slotX: number, slotY: number, container: Container, manualCallback: (artifactId:string) => void) {
        super(scene, canvas, slotX, slotY, container);
        this.setScale(1 / CU.S, 1 / CU.S);

        const title = TextUtils.addTextByFamily(this.scene, ui.currName.toUpperCase(), 0, -282, '#ffffff', 16,
            'normal', 420, Fonts.ARIAL_MT)
            .setOrigin(0.5, 0.5);

        let updateText = TextUtils.addTextByFamily(scene, Tr('uluchshit.do.urovnja').toUpperCase() + String(ui.currentLevel + 1) + '?',
            0, -200, '#ffd465', 18, 'normal', 450, Fonts.MYRIADPRO_BOLD);

        let lightFrom = SpriteUtils.addSprite(this.scene, -90, -120, Keys.ATLAS_PROFILE, Keys.UI_ART_LIGHT, 0.8);
        let lightTo = SpriteUtils.addSprite(this.scene, 90, -120, Keys.ATLAS_PROFILE, Keys.UI_ART_LIGHT, 0.8);
        let itemFrom = SpriteUtils.addSprite(this.scene, -90, -120, ui.atlasKey, ui.currImageKey);
        let fromText = TextUtils.addTextAdvanced(scene, ArtifactUtils.getEffectText(ui.effectType, ui.currLevelValue), -90, -67,
            '#e0e0e0', 15, 'normal', 150, 'center', 2, Fonts.ARIAL_MT);
        let itemArr = SpriteUtils.addSprite(this.scene, 0, -120, Keys.ATLAS_PROFILE, Keys.UI_ART_ARR);
        let itemTo = SpriteUtils.addSprite(this.scene, 90, -120, ui.atlasKey, ui.nextImageKey);
        let toText = TextUtils.addTextAdvanced(scene, ArtifactUtils.getEffectText(ui.effectType, ui.nextLevelValue), 90, -67,
            '#85ff85', 15, 'normal', 150, 'center', 2, Fonts.ARIAL_MT);

        let timeLabel = TextUtils.addTextAdvanced(scene, Tr('vremja.uluchshenija'), 0, -10,
            '#716f6a', 12, 'normal', 250, 'center', 2, Fonts.ARIAL_MT);

        let timeText = TextUtils.addTextAdvanced(scene, TimeUtils.getDurationText(ui.updateTimeSec), 20, 20,
            '#ae9d26', 17, 'normal', 150, 'left', 2, Fonts.MYRIADPRO_BOLD).setOrigin(0.5, 0.5);
        let timeIcon = SpriteUtils.addSprite(this.scene, -timeText.width/2, 20, Keys.ATLAS_PROFILE, Keys.UI_ART_TIME_ICON);

        let itemY = 122;
        let canUpdate = true;
        let optionalAlert = '';
        for (let rr of ui.requiredRunes) {
            let ri: RequiredItem = new RequiredItem(scene, canvas, 0, itemY * CU.S, rr.atlasKey, rr.imageKey, rr.name, rr.present);
            canUpdate = canUpdate && rr.present;
            if (!rr.present) {
                ri.setAlpha(0.4);
                optionalAlert = Tr('dlja.uluchshenija.nuzhna') + rr.name + '!';
            }
            this.add(ri);
            itemY += 40;
        }
        if (ui.minLevel) {
            let player = PlayerStore.getPlayer();
            let ri: RequiredItem = new RequiredItem(scene, canvas, 0, itemY * CU.S, Keys.ATLAS_PROFILE, Keys.UI_LEVEL_UP, String(ui.minLevel) + ' ' + Tr('y.uroven'), player.level >= ui.minLevel);
            canUpdate = canUpdate && player.level >= ui.minLevel;
            if (player.level < ui.minLevel) {
                optionalAlert = Tr('dlja.uluchshenija.neobhodim') + String(ui.minLevel) + ' ' +Tr('y.uroven') + '!';
            }
            this.add(ri);
        }

        let requiredLabel = (ui.requiredRunes && ui.requiredRunes.length > 0) || ui.minLevel;
        let runesLabel = TextUtils.addTextAdvanced(scene, requiredLabel ? Tr('neobhodimo') : (Tr('maks.dot') + ' ' + ArtifactUtils.getEffectText(ui.effectType, ui.maxLevelValue)).toUpperCase(), 0, 90,
             canUpdate ? '#716f6a' : '#ff5e5e', 12, 'normal', 1250, 'center', 2, Fonts.ARIAL_MT);

        let takeOffButton = new SquareButton(this.scene, -80, 220, 0, Keys.UI_ART_PALE_BUTTON,
            Keys.ATLAS_PROFILE, Keys.UI_ART_TAKE_OFF, Tr('peredat'), true);

        let improveButton = new SquareButton(this.scene, 80, 220, -15 * CU.S,
            canUpdate ? Keys.UI_ART_GREEN_BUTTON : Keys.UI_ART_RED_BUTTON, Keys.ATLAS_PROFILE, Keys.UI_ART_UPPER_ICON, Tr('uluchshit'), false);

        this.add(takeOffButton);
        this.add(improveButton);

        takeOffButton.setCallback(() => {
            if (this.isOnTop()) {
                takeOffCallback();
            }
        });

        improveButton.setCallback(() => {
            if (this.isOnTop()) {
                if (canUpdate) {
                    improveCallback();
                } else {
                    cantUpdateCallback(optionalAlert);
                }
            }
        });

        const pricePanel: PricePanel = new PricePanel(scene, 124 * CU.S, 270 * CU.S, ui.amount);
        pricePanel.setOrigin(0.5, 0.5);

        this.add([title, updateText, lightFrom, lightTo, itemFrom, itemArr, itemTo, fromText, toText,
            runesLabel,
            timeLabel, timeIcon, timeText, takeOffButton, improveButton, pricePanel]);

        let statIcon = SpriteUtils.addImage(this.scene!, 71, 21, Keys.ATLAS_UI, Keys.UI_STAT_MINE_CIRCLE);
        statIcon.setInteractive().on('pointerup', function (pointer: any) {
            OnceCaller.doOnce('artManual', () => {
                manualCallback(ui.artifactId);
            });
        });
        this.add(statIcon);
    }

    public open(){
        super.open();
        if(BottomForcesPanel.instance){
            BottomForcesPanel.instance.visible = false;
        }
    }

    public close(){
        super.close();
        if(BottomForcesPanel.instance){
            BottomForcesPanel.instance.visible = true;
        }
    }
}

class RequiredItem extends Phaser.GameObjects.Container {
    constructor(scene: Scene, canvas: HTMLCanvasElement, x: number, y: number, atlasKey: string | undefined, iconKey: string, name: string, existing: boolean) {
        super(scene);
        this.x = x;
        this.y = y;

        let valueText = TextUtils.addTextAdvancedNoWidth(scene, name, 0, 0,
            '#ae9d26', 16, 'normal', 400, 'center', 2, Fonts.ARIAL_MT);

        let icon;
        if (atlasKey) {
            icon = SpriteUtils.addSprite(this.scene, valueText.getLeftCenter().x! / CU.S - 22, 0, atlasKey, iconKey);
            SpriteUtils.setDispSize(icon, 32 * CU.S, 32 * CU.S);
        } else {
            icon = SpriteUtils.addSprite(this.scene, valueText.getLeftCenter().x! / CU.S - 22, 0, iconKey);
            SpriteUtils.setDispSize(icon, 32 * CU.S, 32 * CU.S);
        }

        let existIcon = SpriteUtils.addSprite(this.scene, valueText.getRightCenter().x! / CU.S + 26, 0, Keys.ATLAS_PROFILE, RequiredItem.getExistanceSign(existing));
        SpriteUtils.setDispSize(existIcon, 32 * CU.S, 32 * CU.S);

        this.add([icon, valueText, existIcon]);
    }

    private static getExistanceSign(existing: boolean): string {
        return existing ? Keys.UI_CHECK_SIGN : Keys.UI_CROSS_SIGN;
    }

}
